import * as Sentry from '@sentry/react';

// Initialize Sentry for React frontend
export const initSentry = () => {
  const dsn = process.env.REACT_APP_SENTRY_DSN || "https://8bfba6e28f03360c3dbee0e9e5ffc472@o4509625057280000.ingest.us.sentry.io/4509625062588416";
  const hostname = window.location.hostname;
  let environment = 'development';
  
  if (hostname.includes('test.pmsonline-plx.com')) {
    environment = 'staging';
  } else if (hostname.includes('pmsonline-plx.com')) {
    environment = 'production';
  } else if (hostname.includes('staging')) {
    environment = 'staging';
  }

  Sentry.init({
    dsn,
    
    // Enable performance monitoring and session replay with full capture
    integrations: [
      Sentry.browserTracingIntegration(),
      Sentry.replayIntegration({
        // Full capture (per current requirement)
        maskAllText: false,
      }),
    ],

    // Higher visibility sampling (prod is low-traffic)
    tracesSampleRate: 1.0,          // 100% traces
    profilesSampleRate: 0.5,        // 50% profiles
    replaysSessionSampleRate: 0.5,  // 50% sessions
    replaysOnErrorSampleRate: 1.0,  // all error replays

    // Disable debug mode in production
    debug: environment === 'development',

    // Set environment
    environment,

    // TEMP: Do not filter events; capture everything to increase visibility.
    // beforeSend(event) {
    //   return event;
    // },

    // Capture all breadcrumbs for better debugging
    beforeBreadcrumb(breadcrumb) {
      return breadcrumb;
    },

    // Full configuration for debugging
    attachStacktrace: true,
    maxBreadcrumbs: 100,
  });
};

// Export Sentry for use in components
export { Sentry };

// Helper function to capture API errors (only 400 and 500)
export const captureApiError = (error: any, context?: any) => {
  // Only capture if it's a 400 or 500 error
  if (error.response) {
    const status = error.response.status;
    if (status >= 400 && status < 500) {
      Sentry.withScope((scope) => {
        scope.setTag('error_type', 'api_error');
        scope.setContext('api_request', {
          url: error.config?.url,
          method: error.config?.method,
          status: error.response?.status,
          statusText: error.response?.statusText,
          data: error.config?.data,
          headers: error.config?.headers,
        });
        
        if (error.response?.data) {
          scope.setContext('api_response', {
            data: error.response.data,
            headers: error.response.headers,
          });
        }
        
        if (context) {
          scope.setContext('custom_context', context);
        }

        Sentry.captureException(error);
      });
    } else if (status >= 500) {
      Sentry.withScope((scope) => {
        scope.setTag('error_type', 'api_error');
        scope.setContext('api_request', {
          url: error.config?.url,
          method: error.config?.method,
          status: error.response?.status,
          statusText: error.response?.statusText,
          data: error.config?.data,
          headers: error.config?.headers,
        });
        
        if (error.response?.data) {
          scope.setContext('api_response', {
            data: error.response.data,
            headers: error.response.headers,
          });
        }
        
        if (context) {
          scope.setContext('custom_context', context);
        }

        Sentry.captureException(error);
      });
    }
  }
};

// Helper function to capture user actions (full capture)
export const captureUserAction = (action: string, data?: any) => {
  Sentry.addBreadcrumb({
    category: 'user_action',
    message: action,
    data,
    level: 'info',
  });
};

// Helper function to capture validation errors (only for 400 errors)
export const captureValidationError = (formName: string, errors: any) => {
  Sentry.withScope((scope) => {
    scope.setTag('error_type', 'validation_error');
    scope.setContext('form_validation', {
      form_name: formName,
      errors,
      user_agent: navigator.userAgent,
    });
    
    Sentry.captureMessage(`Form validation failed: ${formName}`, 'warning');
  });
};

// Helper function to capture authentication errors
export const captureAuthError = (error: any, action: string) => {
  Sentry.withScope((scope) => {
    scope.setTag('error_type', 'authentication_error');
    scope.setContext('auth_error', {
      action,
      error_message: error.message,
      error_code: error.code,
      user_agent: navigator.userAgent,
    });
    
    Sentry.captureException(error);
  });
}; 